/**
 * 🎨 EDITABLE WIDGET WRAPPER
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Wraps widgets in EDIT mode with controls (remove, duplicate)
 * Shows controls only on hover to maintain clean UI
 */

import React, { useState } from 'react';
import { X, Copy } from 'lucide-react';
import { ExecutiveWidget } from '../types';

interface EditableWidgetWrapperProps {
  widget: ExecutiveWidget;
  isEditing: boolean;
  isNew?: boolean; // Highlight newly duplicated widgets
  onRemove: (widgetId: string) => void;
  onDuplicate: (widgetId: string) => void;
  children: React.ReactNode;
}

const EditableWidgetWrapper: React.FC<EditableWidgetWrapperProps> = ({
  widget,
  isEditing,
  isNew = false,
  onRemove,
  onDuplicate,
  children
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleRemoveClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmRemove = () => {
    onRemove(widget.id);
    setShowConfirm(false);
  };

  const handleCancelRemove = () => {
    setShowConfirm(false);
  };

  const handleDuplicate = () => {
    onDuplicate(widget.id);
  };

  if (!isEditing) {
    return <>{children}</>;
  }

  return (
    <div
      className={`editable-widget-wrapper ${isNew ? 'widget-highlight' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}

      {/* Edit controls (visible on hover) */}
      {isHovered && !showConfirm && (
        <div className="widget-edit-controls">
          <button
            className="widget-control-btn widget-control-duplicate"
            onClick={handleDuplicate}
            title="Duplicate widget"
          >
            <Copy size={16} />
          </button>
          
          <button
            className="widget-control-btn widget-control-remove"
            onClick={handleRemoveClick}
            title="Remove widget"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Remove confirmation tooltip */}
      {showConfirm && (
        <div className="widget-remove-confirm">
          <div className="widget-remove-confirm-content">
            <p>Remove <strong>{widget.title}</strong>?</p>
            <div className="widget-remove-confirm-actions">
              <button
                className="btn-confirm"
                onClick={handleConfirmRemove}
              >
                Confirm
              </button>
              <button
                className="btn-cancel"
                onClick={handleCancelRemove}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditableWidgetWrapper;
